import { FC, useCallback, useMemo, useState } from 'react';

import { API_URL } from 'constant';
import { REACT_APP_API_HOST as API_HOST } from 'envs';
import { Blank, FileDrop, Kebab, Modal, PageHead, Pager } from 'views/design-system';

import { Empty, TradeAmountCell } from '../bits';
import { ago, dateLine, hms } from '../data/format';
import { IImportBatch, IImportStatus } from '../data/types';
import {
	IMPORTS_PAGE_SIZE,
	MAX_UPLOAD_BYTES,
	useFxRates,
	useImportBatch,
	useImportBatches,
	useImportFindings,
} from '../data/use-tm';

import '../../design-system/tokens.scss';
import '../../design-system/surface.scss';
import '../txn-surface.scss';
import './historical-deck.scss';

/**
 * TRANSACTION MONITORING — HISTORICAL DATA.
 *
 * A new client uploads their back-book as CSV and the same rules engine that
 * scores live traffic back-scores it. Built to
 * `_design/specs/27-tm-historical-data.md`.
 *
 * THE THREE THINGS THIS PAGE HAS TO SAY OUT LOUD, because a client cannot infer
 * any of them and each one changes what they do:
 *
 * 1. **Order does not matter.** The server sorts every row chronologically
 *    before scoring any of them, because several rules read each subject's own
 *    recent history. Saying so stops a client hand-sorting a two-million-row
 *    export before uploading it.
 * 2. **Findings do not go to the live queue.** A back-scored row that needs
 *    review is recorded here, with no SLA clock, and reaches an analyst's queue
 *    only if somebody promotes it. Without saying this, a compliance officer
 *    reasonably assumes uploading two years of history will page the on-call
 *    desk.
 * 3. **It costs nothing.** Scoring is our own engine — no vendor is called and
 *    nothing is billed however much history is sent. This is the first question
 *    anyone asked to upload their whole ledger will have.
 */

const STATUSES: (IImportStatus | 'All')[] = [
	'All',
	'queued',
	'validating',
	'importing',
	'needs_attention',
	'done',
	'partially_failed',
	'cancelled',
	'failed',
];

/** How a status reads, and which tone it carries. */
const STATUS_LABEL: Record<IImportStatus, string> = {
	queued: 'Queued',
	validating: 'Validating…',
	importing: 'Importing',
	needs_attention: 'Needs attention',
	done: 'Done',
	partially_failed: 'Partially failed',
	cancelled: 'Cancelled',
	failed: 'Failed',
};
const STATUS_TONE: Record<IImportStatus, string> = {
	queued: 'wait',
	validating: 'wait',
	importing: 'rev',
	needs_attention: 'warn',
	done: 'ok',
	partially_failed: 'warn',
	cancelled: 'gy',
	failed: 'rej',
};

const RUNNING: IImportStatus[] = ['queued', 'validating', 'importing'];
const isRunning = (b: IImportBatch) => RUNNING.includes(b.status);

/* The minimum a client must supply, and what each extra tier unlocks. Kept in
   sync with the parser's own REQUIRED/KNOWN lists by the template download —
   which is served BY THE BACKEND from those same constants, so this table
   documents the contract rather than restating it as a second source. */
const TIERS = [
	{
		tier: 'Required',
		columns: 'txnId · subjectVendorData · direction · amount · currency · txnDate',
		note: 'A row missing any of these cannot be scored and is rejected.',
	},
	{
		tier: 'Strongly recommended',
		columns: 'preferredCurrencyAmount · counterpartyName · counterpartyCountry · currencyKind',
		note: 'Every amount-threshold rule reads preferredCurrencyAmount, never amount — supply the converted figure if your book is in mixed currencies.',
	},
	{
		tier: 'Device intelligence',
		columns: 'device.fingerprint · device.ipCountryCode · device.isVpnOrTor · device.isDataCenter · device.asnOrganization',
		note: 'Unlocks the multi-accounting and shared-device rules. Without a fingerprint those cannot fire at all.',
	},
	{
		tier: 'Trade legs',
		columns:
			'transactionKind · sentLeg.amount · sentLeg.currency · sentLeg.currencyKind · sentLeg.preferredCurrencyAmount · receivedLeg.* (same shape)',
		note: 'Only needed when transactionKind is trade — a payment (the default) never reads these and leaves them blank. The two legs may be different currencies AND different kinds (BTC sent, USD received); the reportable amount every threshold rule reads is the GREATER of the two.',
	},
	{
		tier: 'Fees & totals',
		columns:
			'feeAmount · feeCurrency · feePreferredCurrencyAmount · sentLeg.feeAmount · receivedLeg.feeAmount (a venue may charge both sides of a trade)',
		note: 'Every transaction has three figures: the PRINCIPAL (amount, or the two legs on a trade), the TOTAL FEE, and the TOTAL TRANSACTION AMOUNT. Send the first two — the total is COMPUTED as principal + fee and is never read from the file, because three figures that can disagree is a reconciliation bug waiting to happen. Leave the fee columns blank if you do not charge or do not have them: blank means "not reported" and is not the same as zero. Rules score the PRINCIPAL, never the total — a fee is your revenue, not value moved, and letting it push a $9,900 transfer over a $10,000 reporting line would flag on your own pricing.',
	},
	{
		tier: 'Travel rule (FATF R.16)',
		columns: 'travelRule.counterpartyVaspIdentified · travelRule.originatorInfoComplete · travelRule.beneficiaryInfoComplete · travelRule.originatorNameMatchesKyc',
		note: 'Leave a flag BLANK when you have not assessed it. These rules match only on an explicit false, so a blank is "unknown" and never counts against the transfer.',
	},
];

/**
 * The template is a real file download, so it is an ordinary link — and an
 * ordinary link needs the API HOST in front of it. `API_URL.*` are paths that
 * `useNetwork` prefixes for you; a bare one in an `href` would resolve against
 * the panel's own origin and 404. It needs no auth: the template is a blank
 * header plus two invented example rows, identical for every tenant.
 */
const TEMPLATE_HREF = `${API_HOST}${API_URL.TM_IMPORT_TEMPLATE}`;

const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;

/** `4m 12s`, `38s` — never `252 seconds`, which nobody reads as four minutes. */
const eta = (secs: number | null): string => {
	if (secs === null || !Number.isFinite(secs) || secs < 0) return 'estimating…';
	if (secs < 1) return 'almost done';
	if (secs < 60) return `${Math.ceil(secs)}s left`;
	const m = Math.floor(secs / 60);
	const rest = Math.ceil(secs % 60);
	return rest ? `${m}m ${rest}s left` : `${m}m left`;
};

const rate = (bps: number): string =>
	bps > 0 ? `${mb(bps)}/s` : '';

const pct = (b: IImportBatch) =>
	b.rowsTotal > 0 ? Math.min(100, Math.round((b.rowsProcessed / b.rowsTotal) * 100)) : 0;

export const HistoricalDeck: FC = () => {
	const [statusIdx, setStatusIdx] = useState(0);
	const [q, setQ] = useState('');
	const [pageNo, setPageNo] = useState(1);
	const [howOpen, setHowOpen] = useState(false);
	const [columnsOpen, setColumnsOpen] = useState(false);
	const [uploadOpen, setUploadOpen] = useState(false);
	const [viewId, setViewId] = useState<string | null>(null);
	const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
	const [busy, setBusy] = useState<string | null>(null);
	/* The delete refusal, shown in the confirmation itself. The reason IS the
	   answer ("N findings are live work"), so it belongs where the action was
	   taken rather than in a toast that disappears. */
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [fxOpen, setFxOpen] = useState(false);
	const [fxCsv, setFxCsv] = useState('');
	const [fxMsg, setFxMsg] = useState<string | null>(null);
	const [correctFile, setCorrectFile] = useState<File | null>(null);
	const [correctResult, setCorrectResult] = useState<any>(null);
	const [correctBusy, setCorrectBusy] = useState(false);
	const [bulkMsg, setBulkMsg] = useState<string | null>(null);

	/* Upload state */
	const [file, setFile] = useState<File | null>(null);
	const [label, setLabel] = useState('');
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [uploading, setUploading] = useState(false);
	/* The batch the modal is currently watching import. Distinct from `viewId`
	   (the detail modal): this keeps the UPLOAD modal open through the slow half
	   so the client sees one continuous progress bar instead of a bar that
	   vanishes the moment the bytes land. */
	const [importingId, setImportingId] = useState<string | null>(null);

	const filters = useMemo(
		() => ({ status: STATUSES[statusIdx] as string, q, pageNo }),
		[statusIdx, q, pageNo]
	);
	const {
		batches, total, loading, failed, upload, progress, setProgress,
		act, destroy, errorReport, correct,
	} = useImportBatches(filters);
	const { pairs, rates, addRates, removeRate } = useFxRates();
	const { batch: detail } = useImportBatch(viewId);
	/* Polled while the upload modal is watching it — same hook the detail panel
	   uses, so there is one definition of "still running". */
	const { batch: importing } = useImportBatch(importingId);
	/* Findings are told whether the import is still running, so they keep
	   refreshing while rows are being scored — the detail panel opens the moment
	   an upload is accepted, which is before anything has been scored at all. */
	const { findings, promote, promoteBulk } = useImportFindings(
		viewId,
		!!detail && RUNNING.includes(detail.status)
	);

	const pages = Math.max(1, Math.ceil(total / IMPORTS_PAGE_SIZE));
	const inFlight = batches.filter(isRunning).length;
	const deleting = batches.find(b => b._id === confirmDeleteId) ?? null;

	/* The import half's ETA, from the rate the SERVER reports rather than one
	   guessed here: the runner knows how many rows it has actually written and
	   how long it took, and rows/sec varies by an order of magnitude with how
	   much history each subject already has. */
	/**
	 * The bulk-promotion options, built from the severities this import's
	 * findings ACTUALLY have.
	 *
	 * Ordered worst-first, and each tier includes everything above it, so the
	 * buttons read as "how far down the list do I go" — which is the decision an
	 * analyst is making — rather than as an arbitrary pair of presets. Only
	 * findings still historical are counted: promoting one should remove it from
	 * the tally the next button offers.
	 */
	const bulkTiers = useMemo(() => {
		const order = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'UNKNOWN'];
		const present = new Set(
			findings
				.filter(f => f.historical)
				.map(f => ((f.linkedTransactionId as any)?.severity as string) ?? 'UNKNOWN')
		);
		const ranked = order.filter(sv => present.has(sv));
		return ranked.map((sv, i) => {
			const severities = ranked.slice(0, i + 1);
			const n = findings.filter(
				f =>
					f.historical &&
					severities.includes(
						((f.linkedTransactionId as any)?.severity as string) ?? 'UNKNOWN'
					)
			).length;
			return {
				label: `${severities.length === 1 ? sv.toLowerCase() : `${sv.toLowerCase()} and above`} (${n})`,
				severities,
			};
		});
	}, [findings]);

	const importEta = useMemo(() => {
		if (!importing || !importing.ratePerSec) return null;
		const left = Math.max(0, importing.rowsTotal - importing.rowsProcessed);
		return left / importing.ratePerSec;
	}, [importing]);

	const resetUpload = useCallback(() => {
		setFile(null);
		setLabel('');
		setUploadError(null);
		setUploading(false);
		setImportingId(null);
		setProgress({
			phase: 'idle',
			loaded: 0,
			total: 0,
			bytesPerSec: 0,
			etaSeconds: null,
		});
	}, [setProgress]);

	/**
	 * Read the file in the browser and post its text.
	 *
	 * The whole file goes in one request because the server must hold every row
	 * before it writes any of them — it sorts chronologically first, and a row
	 * written before its own predecessors would be scored against a history that
	 * had not happened yet. So there is nothing to gain from chunking, and
	 * chunking would break the ordering guarantee.
	 */
	const startImport = useCallback(async () => {
		if (!file) return;
		setUploading(true);
		setUploadError(null);
		setProgress({
			phase: 'reading',
			loaded: 0,
			total: file.size,
			bytesPerSec: 0,
			etaSeconds: null,
		});
		try {
			const text = await file.text();
			const res: any = await upload(label.trim() || file.name, file.name, text);
			const created = res?.data ?? res;
			/* A bad HEADER comes back as a `failed` batch WITH its reason rather
			   than as an HTTP error — the attempt is still recorded in the log, and
			   the reason is the one thing the client needs to fix the file. */
			if (created?.headerError) {
				setUploadError(created.headerError);
				setUploading(false);
				return;
			}
			/* The modal STAYS OPEN and switches to the import phase. Closing it the
			   moment the bytes land would replace a progress bar with a log row at
			   exactly the point the slow half starts — the upload is seconds, the
			   back-scoring is minutes. */
			if (created?._id) setImportingId(String(created._id));
			setUploading(false);
		} catch (err: any) {
			setUploadError(
				err?.message ??
					'The upload failed. If the file is very large it may have been refused by the gateway before it reached the importer.'
			);
			setUploading(false);
		}
	}, [file, label, upload, resetUpload]);

	/**
	 * Turn the report the service returns into a file the browser saves.
	 *
	 * It cannot be a plain `<a href>`: the report sits behind the same ownership
	 * check as the batch, and the request needs the auth header `useNetwork`
	 * attaches — so it is fetched through the hook and handed to the browser as a
	 * Blob. The object URL is revoked on the next tick rather than immediately;
	 * revoking it in the same statement as `.click()` can cancel the download in
	 * Safari before it starts.
	 */
	const downloadErrors = useCallback(
		async (batchId: string, name: string) => {
			const report = await errorReport(batchId);
			if (!report) return;
			const url = URL.createObjectURL(new Blob([report.csv], { type: 'text/csv' }));
			const a = document.createElement('a');
			a.href = url;
			a.download = report.filename || `${name}-errors.csv`;
			a.click();
			setTimeout(() => URL.revokeObjectURL(url), 0);
		},
		[errorReport]
	);

	const run = useCallback(
		async (batchId: string, body: Record<string, unknown>) => {
			setBusy(batchId);
			try {
				await act(batchId, body);
			} finally {
				setBusy(null);
			}
		},
		[act]
	);

	return (
		<div className="sv2-scope sv2 txn-deck hist-deck">
			<PageHead
				title="Transaction monitoring — historical data"
				sub={
					<>
						{dateLine()} · {total} import{total === 1 ? '' : 's'}
						{inFlight > 0 && <span className="bl"> · {inFlight} in progress</span>} ·
						back-scoring runs on our own engine — no vendor is called and nothing is billed
						{failed && (
							<span className="rd"> · import log refresh failed, showing the last good list</span>
						)}
					</>
				}
				action={
					<button className="cta" type="button" onClick={() => setUploadOpen(true)}>
						Upload historical data
					</button>
				}
			/>

			{/* THE ON-PAGE DOCUMENTATION. Collapsed by default so it does not push
			    the log down for someone who has already read it, but on the page
			    rather than in a separate doc — a client uploading for the first time
			    should not have to be sent a link. */}
			<div className="panel hist-how">
				<button
					className="hist-how__head"
					type="button"
					aria-expanded={howOpen}
					onClick={() => setHowOpen(o => !o)}
				>
					How this works {howOpen ? '▾' : '▸'}
					<span className="r">the data model, the order, and what happens to findings</span>
				</button>
				{howOpen && (
					<div className="hist-how__body">
						<div className="hist-how__grid">
							<div>
								<div className="grp">Order does not matter</div>
								<p>
									Upload in any order. Every row is sorted by transaction date before
									anything is scored, because several rules read each subject&apos;s own
									recent history — a transaction scored before the ones that came before it
									would be measured against a history that had not happened yet. You do not
									need to sort your export.
								</p>
							</div>
							<div>
								<div className="grp">What it costs</div>
								<p>
									Nothing. Back-scoring runs entirely on our own rules engine: no vendor is
									called, no third-party check is performed, and no per-transaction charge
									applies however much history you send.
								</p>
							</div>
							<div>
								<div className="grp">What happens to findings</div>
								<p>
									A row that would need review is recorded as a <b>finding</b> on this page —
									not sent to the live alert queue, and carrying no SLA clock. That is
									deliberate: a two-year backfill would otherwise arrive in an
									analyst&apos;s queue as thousands of alerts that all read as already
									overdue. You can send any single finding to live triage if it still needs
									action.
								</p>
							</div>
							<div>
								<div className="grp">Corrections</div>
								<p>
									To fix a bad upload, delete the import and upload again — a transaction id
									is unique, so re-uploading a corrected row on its own would be ignored as
									a duplicate. Deleting an import removes everything it added.
								</p>
							</div>
						</div>

						<div className="grp">The columns</div>
						<table className="stbl hist-tiers">
							<thead>
								<tr>
									<th>Tier</th>
									<th>Columns</th>
								</tr>
							</thead>
							<tbody>
								{TIERS.map(t => (
									<tr key={t.tier}>
										<td>
											<span className="hist-tier">{t.tier}</span>
										</td>
										<td>
											<span className="mono hist-cols">{t.columns}</span>
											<span className="hist-note">{t.note}</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>

						<div className="hist-how__acts">
							<a className="dbtn pri" href={TEMPLATE_HREF} download>
								Download CSV template
							</a>
							<button className="dbtn" type="button" onClick={() => setColumnsOpen(o => !o)}>
								{columnsOpen ? 'Hide' : 'Show'} the full column reference
							</button>
						</div>

						{columnsOpen && (
							<div className="hist-ref">
								<p>
									The template above carries the exact header the importer expects, in order,
									with two example rows. Anything not listed in it is ignored with a warning
									rather than rejected, so an export with extra columns still imports.
								</p>
								<ul>
									<li>
										<b>Dates</b> — ISO-8601 (<span className="mono">2019-03-01T14:22:00Z</span>
										) is unambiguous and preferred. <span className="mono">03/04/2019</span> is
										accepted but read <b>day-first</b> and flagged as a warning, because it
										means two different days depending on who wrote it.
									</li>
									<li>
										<b>Amounts</b> — thousands separators, currency symbols and
										parenthesised negatives are all understood.{' '}
										<span className="mono">&quot;1,234.56&quot;</span>,{' '}
										<span className="mono">$1234.56</span> and{' '}
										<span className="mono">(1234.56)</span> all parse.
									</li>
									<li>
										<b>Booleans</b> — <span className="mono">true/false</span>,{' '}
										<span className="mono">yes/no</span>, <span className="mono">1/0</span>.
										A blank stays <i>unset</i>, which is not the same as false.
									</li>
									<li>
										<b>Duplicate transaction ids</b> within one file are rejected rather than
										silently de-duplicated, because that usually means the export itself is
										wrong.
									</li>
								</ul>
							</div>
						)}
					</div>
				)}
			</div>

			<div className="ctl">
				<button
					className={`pre${statusIdx ? ' on' : ''}`}
					type="button"
					onClick={() => {
						setStatusIdx(i => (i + 1) % STATUSES.length);
						setPageNo(1);
					}}
				>
					Status:{' '}
					<b>
						{statusIdx === 0
							? 'All'
							: STATUS_LABEL[STATUSES[statusIdx] as IImportStatus]}
					</b>{' '}
					▾
				</button>
				<label className="srch" title="Searches the import name and filename">
					<svg viewBox="0 0 24 24">
						<circle cx="11" cy="11" r="7" />
						<line x1="16.5" y1="16.5" x2="21" y2="21" />
					</svg>
					<input
						placeholder="Search imports"
						value={q}
						onChange={e => {
							setQ(e.target.value);
							setPageNo(1);
						}}
					/>
				</label>
				<button className="pre" type="button" onClick={() => setFxOpen(true)}>
					FX rates <b>{pairs.length || '·'}</b>
				</button>
				<span className="rn">
					{batches.length} of {total} imports
				</span>
			</div>

			<div className="panel">
				<div className="ph">
					Import history
					<span className="r">
						{total} total
						{pages > 1 && <Pager page={pageNo} pages={pages} onChange={setPageNo} />}
					</span>
				</div>
				<div className="tscroll">
					<table className="stbl hist-tbl">
						<thead>
							<tr>
								<th>Import</th>
								<th className="ht-rows">Rows</th>
								<th className="ht-status">Status</th>
								<th className="ht-find">Findings</th>
								<th className="ht-act">Actions</th>
							</tr>
						</thead>
						<tbody>
							{!batches.length && (
								<tr>
									<td colSpan={5} className="empty">
										{loading
											? 'Loading the import log…'
											: failed
												? 'Could not reach transaction monitoring.'
												: q || statusIdx
													? 'No import matches these filters.'
													: 'No imports yet.'}
									</td>
								</tr>
							)}
							{batches.map(b => (
								<tr key={b._id}>
									<td>
										<span className="ht-name">{b.label}</span>
										<span className="ht-sub">
											<span className="mono">{b.filename}</span>
											{b.uploadedBy && ` · ${b.uploadedBy}`}
											{b.createdAt && ` · ${ago(new Date(b.createdAt).getTime())} ago`}
										</span>
									</td>
									<td className="ht-rows">
										<b className="mono">{b.rowsImported.toLocaleString()}</b>
										<span className="ht-rowsub">
											imported
											{b.rowsAlreadyExisted > 0 &&
												` · ${b.rowsAlreadyExisted.toLocaleString()} already there`}
											{b.rowsRejected > 0 && (
												<span className="rd"> · {b.rowsRejected.toLocaleString()} rejected</span>
											)}
											{b.rowsWarned > 0 && (
												<span className="am"> · {b.rowsWarned.toLocaleString()} warned</span>
											)}
										</span>
									</td>
									<td className="ht-status">
										<span className={`vch ${STATUS_TONE[b.status]}`}>
											{STATUS_LABEL[b.status]}
											{b.status === 'importing' && ` ${pct(b)}%`}
										</span>
										{isRunning(b) && (
											<span className="ht-bar">
												<i style={{ width: `${pct(b)}%` }} />
											</span>
										)}
										{b.status === 'importing' && b.ratePerSec > 0 && (
											<span className="ht-rate mono">
												{b.ratePerSec.toLocaleString()} rows/sec
											</span>
										)}
										{b.pauseReason && !isRunning(b) && (
											<span className="ht-reason">{b.pauseReason}</span>
										)}
									</td>
									<td className="ht-find">
										{isRunning(b) ? (
											<span className="gy">—</span>
										) : b.alertsRaised > 0 ? (
											<button
												className="linky"
												type="button"
												onClick={() => setViewId(b._id)}
											>
												{b.alertsRaised.toLocaleString()}
											</button>
										) : (
											<span className="gy">none</span>
										)}
									</td>
									<td className="ht-act">
										<div className="acts">
											<button className="dbtn" type="button" onClick={() => setViewId(b._id)}>
												View
											</button>
											<Kebab
												items={[
													...(b.rowsRejected > 0
														? [
																{
																	label: 'Download error report',
																	onClick: () => downloadErrors(b._id, b.label),
																},
															]
														: []),
													...(isRunning(b)
														? [
																{
																	label: b.cancelRequested ? 'Cancelling…' : 'Cancel import',
																	disabled: b.cancelRequested || busy === b._id,
																	onClick: () => run(b._id, { cancel: true }),
																},
															]
														: []),
													...(b.status === 'needs_attention'
														? [
																{
																	label: 'Abandon this import',
																	onClick: () => run(b._id, { abandon: true }),
																},
															]
														: []),
													{
														label: 'Delete import',
														disabled: isRunning(b),
														...(isRunning(b)
															? {
																	hint: 'This import is still running. Cancel it first — deleting a run mid-write would leave half a book behind.',
																}
															: {}),
														onClick: () => setConfirmDeleteId(b._id),
													},
												]}
											/>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{!loading && !batches.length && !q && !statusIdx && (
				<Blank
					title="No historical data imported yet"
					description={
						<>
							Upload a CSV of past transactions and the same rules that score live traffic
							will score them. Nothing is sent to a vendor and nothing is billed. Start with
							the template — it carries the exact header the importer expects.
						</>
					}
					action={
						<button className="cta" type="button" onClick={() => setUploadOpen(true)}>
							Upload historical data
						</button>
					}
				/>
			)}

			<div className="foot">
				An import is scored by the same engine, in the same order, as live traffic: rows are
				sorted by transaction date and each one is measured against the history that existed
				at that moment. Findings carry no SLA and never enter the live queue unless promoted —
				so back-loading two years of a ledger cannot bury the alert queue an analyst is
				working. Correcting an upload means deleting the import and re-uploading, because a
				transaction id is unique and a corrected duplicate would be ignored.
			</div>

			{/* ── upload ─────────────────────────────────────────────────────── */}
			<Modal
				open={uploadOpen}
				title="Upload historical data"
				size="wide"
				onClose={() => {
					setUploadOpen(false);
					resetUpload();
				}}
				sub={
					uploading
						? 'Reading and validating…'
						: 'A .csv of past transactions. Order does not matter — they are sorted before scoring.'
				}
				footer={
					<>
						<a className="dbtn" href={TEMPLATE_HREF} download>
							Download CSV template
						</a>
						{importing && !RUNNING.includes(importing.status) ? (
							/* The import has finished while the modal was open: the useful
							   next action is the summary, not another upload. */
							<button
								className="dbtn pri"
								type="button"
								style={{ marginLeft: 'auto' }}
								onClick={() => {
									const id = importingId;
									setUploadOpen(false);
									resetUpload();
									if (id) setViewId(id);
								}}
							>
								View the import
							</button>
						) : (
							<button
								className="dbtn pri"
								type="button"
								style={{ marginLeft: 'auto' }}
								disabled={!file || uploading || !!importing}
								onClick={startImport}
							>
								{uploading ? 'Uploading…' : importing ? 'Importing…' : 'Start import'}
							</button>
						)}
					</>
				}
			>
				<div className="hist-up">
					{/* THE PROGRESS PANEL. Two phases, one bar — uploading the bytes,
					    then back-scoring the rows. They are shown as one continuous
					    thing because to the client they are one wait, and the second
					    half is much the longer of the two. */}
					{(progress.phase === 'uploading' ||
						progress.phase === 'reading' ||
						importing) && (
						<div className="hist-prog">
							{importing ? (
								<>
									<div className="hist-prog__head">
										<b>
											{importing.status === 'validating'
												? 'Reading and validating…'
												: importing.status === 'queued'
													? 'Queued…'
													: 'Back-scoring rows'}
										</b>
										<span className="mono">
											{importing.rowsProcessed.toLocaleString()} /{' '}
											{importing.rowsTotal.toLocaleString()}
										</span>
									</div>
									<div className="hist-prog__bar">
										<i
											className={
												importing.status === 'validating' ||
												importing.status === 'queued'
													? 'indet'
													: ''
											}
											style={{ width: `${pct(importing)}%` }}
										/>
									</div>
									<div className="hist-prog__foot">
										<span>
											{importing.ratePerSec > 0
												? `${importing.ratePerSec.toLocaleString()} rows/sec`
												: 'starting…'}
										</span>
										<span>{eta(importEta)}</span>
									</div>
									<div className="hist-prog__counts">
										<span className="g">{importing.rowsImported.toLocaleString()} imported</span>
										{importing.rowsAlreadyExisted > 0 && (
											<span>{importing.rowsAlreadyExisted.toLocaleString()} already there</span>
										)}
										{importing.rowsRejected > 0 && (
											<span className="rd">{importing.rowsRejected.toLocaleString()} rejected</span>
										)}
										{importing.rowsWarned > 0 && (
											<span className="am">{importing.rowsWarned.toLocaleString()} warned</span>
										)}
									</div>
								</>
							) : (
								<>
									<div className="hist-prog__head">
										<b>
											{progress.phase === 'reading'
												? 'Reading the file…'
												: 'Uploading'}
										</b>
										<span className="mono">
											{mb(progress.loaded)} / {mb(progress.total)}
										</span>
									</div>
									<div className="hist-prog__bar">
										<i
											className={progress.phase === 'reading' ? 'indet' : ''}
											style={{
												width:
													progress.total > 0
														? `${Math.round((progress.loaded / progress.total) * 100)}%`
														: '100%',
											}}
										/>
									</div>
									<div className="hist-prog__foot">
										<span>{rate(progress.bytesPerSec)}</span>
										<span>
											{progress.phase === 'reading'
												? 'reading from disk'
												: eta(progress.etaSeconds)}
										</span>
									</div>
								</>
							)}
						</div>
					)}

					<FileDrop
						accept=".csv,text/csv"
						file={file}
						onFile={f => {
							/* CHECKED ON SELECTION, not on submit. A client who picks a
							   400 MB export should be told immediately, not after
							   watching it upload. */
							if (f && f.size > MAX_UPLOAD_BYTES) {
								setFile(null);
								setUploadError(
									`That file is ${mb(f.size)}, over the ${mb(MAX_UPLOAD_BYTES)} limit for one import. ` +
										'Split it — by date range is easiest, and the parts can be uploaded in any ' +
										'order because each import sorts its own rows before scoring.'
								);
								return;
							}
							setFile(f);
							setUploadError(null);
							if (f && !label) setLabel(f.name.replace(/\.csv$/i, ''));
						}}
						disabled={uploading}
						label="Drag a .csv here, or click to choose"
						hint={`Up to ${mb(
							MAX_UPLOAD_BYTES
						)}. Required columns: txnId, subjectVendorData, direction, amount, currency, txnDate — for a trade row, send transactionKind=trade with sentLeg.amount, sentLeg.currency, receivedLeg.amount and receivedLeg.currency instead of amount/currency`}
					/>

					<div className="cfg-row">
						<label htmlFor="hist-label">Name this import</label>
						<input
							id="hist-label"
							className="hist-input"
							value={label}
							disabled={uploading}
							placeholder="e.g. 2019–2024 back-book"
							onChange={e => setLabel(e.target.value)}
						/>
					</div>

					{uploadError && (
						<div className="hist-err">
							<b>The file was not imported.</b>
							{uploadError}
						</div>
					)}

					<div className="cfg-note">
						Nothing is billed for back-scoring, however large the file. Rows that cannot be
						scored at all — no id, no subject, no date, no amount, no direction — are
						rejected and listed in a downloadable report; everything else imports, with a
						warning where a column was missing that some rules need.
					</div>
				</div>
			</Modal>

			{/* ── detail ─────────────────────────────────────────────────────── */}
			<Modal
				open={!!viewId}
				title="Import detail"
				size="wide"
				onClose={() => setViewId(null)}
				sub={detail ? detail.label : ''}
			>
				{detail && (
					<div className="hist-detail">
						<div className="grp">Summary</div>
						<table className="kv wide">
							<tbody>
								<tr>
									<td>file</td>
									<td className="mono">{detail.filename}</td>
								</tr>
								<tr>
									<td>status</td>
									<td>
										<span className={`vch ${STATUS_TONE[detail.status]}`}>
											{STATUS_LABEL[detail.status]}
										</span>
									</td>
								</tr>
								{detail.uploadedBy && (
									<tr>
										<td>uploaded by</td>
										<td>{detail.uploadedBy}</td>
									</tr>
								)}
								{detail.startedAt && detail.finishedAt && (
									<tr>
										<td>duration</td>
										<td className="mono">
											{Math.max(
												1,
												Math.round(
													(new Date(detail.finishedAt).getTime() -
														new Date(detail.startedAt).getTime()) /
														1000
												)
											)}
											s
										</td>
									</tr>
								)}
								{detail.ratePerSec > 0 && (
									<tr>
										<td>rate</td>
										<td className="mono">{detail.ratePerSec.toLocaleString()} rows/sec</td>
									</tr>
								)}
								<tr>
									<td>arrived pre-sorted</td>
									<td>
										{detail.sortedOnArrival === null
											? '—'
											: detail.sortedOnArrival
												? 'yes'
												: `no — ${detail.rowsReordered.toLocaleString()} row(s) reordered before scoring`}
									</td>
								</tr>
							</tbody>
						</table>

						<div className="grp">Rows</div>
						<table className="kv wide">
							<tbody>
								<tr>
									<td>in the file</td>
									<td className="mono">{detail.rowsTotal.toLocaleString()}</td>
								</tr>
								<tr>
									<td>imported</td>
									<td className="mono g">{detail.rowsImported.toLocaleString()}</td>
								</tr>
								<tr>
									<td>already there</td>
									<td className="mono">{detail.rowsAlreadyExisted.toLocaleString()}</td>
								</tr>
								<tr>
									<td>rejected</td>
									<td className={`mono${detail.rowsRejected ? ' rd' : ''}`}>
										{detail.rowsRejected.toLocaleString()}
									</td>
								</tr>
								<tr>
									<td>imported with a warning</td>
									<td className={`mono${detail.rowsWarned ? ' am' : ''}`}>
										{detail.rowsWarned.toLocaleString()}
									</td>
								</tr>
							</tbody>
						</table>

						{detail.pauseReason && (
							<div className="hist-err">
								<b>
									{detail.status === 'needs_attention' ? 'Paused' : 'Not imported'}
								</b>
								{detail.pauseReason}
							</div>
						)}

						{!!detail.rejectReasons?.length && (
							<>
								<div className="grp">Why rows were rejected</div>
								<table className="stbl hist-rej">
									<thead>
										<tr>
											<th className="hr-n">Rows</th>
											<th>Reason</th>
											<th className="hr-eg">Example</th>
										</tr>
									</thead>
									<tbody>
										{detail.rejectReasons.map(r => (
											<tr key={r.code}>
												<td className="hr-n mono">{r.count.toLocaleString()}</td>
												<td>{r.detail}</td>
												<td className="hr-eg mono">
													row {r.sampleRow}
													{r.sampleValue && ` · ${r.sampleValue}`}
												</td>
											</tr>
										))}
									</tbody>
								</table>
								<div className="hist-how__acts">
									<button
										className="dbtn"
										type="button"
										onClick={() => downloadErrors(detail._id, detail.label)}
									>
										Download the full error report (.csv)
									</button>
								</div>
							</>
						)}

						<div className="grp">Findings</div>
						{findings.length ? (
							<>
								<div className="hist-note">
									These carry no SLA and are not in the live alert queue. Send one to triage
									only if it still needs action — that gives it a fresh clock starting now.
								</div>
								{/* BULK PROMOTION, by severity only. There is deliberately no
								    "send everything": the risk was never the bulk, it was the
								    CLOCKS — thousands of alerts all due at once is a queue that
								    breaches together and can never be worked. The service
								    staggers the due dates and refuses past its cap rather than
								    silently promoting a prefix. */}
								{/* THE OPTIONS ARE THE SEVERITIES ACTUALLY PRESENT, in
								    descending order — not a hardcoded CRITICAL/HIGH pair. An
								    import whose findings are all MEDIUM would otherwise show
								    two buttons that promote nothing and report "no finding
								    matches", which reads as broken. Each button promotes that
								    severity AND everything above it, so the choice is "how far
								    down do I go", which is the actual decision. */}
								<div className="hist-how__acts">
									{bulkTiers.map(({ label, severities }) => (
										<button
											key={label}
											className="dbtn"
											type="button"
											onClick={async () => {
												setBulkMsg(null);
												const res: any = await promoteBulk(detail._id, severities);
												const body = res?.data ?? res;
												setBulkMsg(
													body?.promoted !== undefined
														? `${body.promoted} finding(s) sent to triage — each with a fresh SLA, staggered so they do not all fall due at once.`
														: (res?.message ?? 'Nothing was promoted.')
												);
											}}
										>
											Send {label} to triage
										</button>
									))}
								</div>
								{bulkMsg && <div className="hist-note">{bulkMsg}</div>}
								<table className="stbl hist-find">
									<thead>
										<tr>
											<th>Subject</th>
											<th className="hf-rule">Why</th>
											{/* An amount column the findings table never had. An
											    analyst reviewing a finding had to open the drawer to
											    see what was actually flagged — a gap before trades
											    existed, and a sharper one now that a finding can be
											    a two-currency conversion. */}
											<th className="hf-amt">Amount</th>
											<th className="hf-score">Score</th>
											<th className="hf-act" />
										</tr>
									</thead>
									<tbody>
										{findings.map(f => {
											const t: any = f.linkedTransactionId ?? {};
											return (
												<tr key={f._id}>
													<td>
														<span className="ht-name">{t.subjectName ?? '—'}</span>
														<span className="ht-sub">
															<span className="mono">{t.txnId ?? ''}</span>
															{t.txnDate &&
																` · ${new Date(t.txnDate).toISOString().slice(0, 10)}`}
														</span>
													</td>
													<td className="hf-rule">
														{f.title}
														<span className="ht-sub">{t.decisionReasonLabel ?? ''}</span>
													</td>
													<td className="hf-amt mono">
														{t.txnId ? <TradeAmountCell txn={t} /> : '—'}
													</td>
													<td className="hf-score mono">{t.score ?? 0}</td>
													<td className="hf-act">
														{f.historical ? (
															<button
																className="dbtn pri"
																type="button"
																onClick={() => promote(f._id)}
															>
																Send to triage
															</button>
														) : (
															<span className="gy">in triage</span>
														)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</>
						) : (
							<Empty>
								{isRunning(detail)
									? 'Still importing — findings appear as rows are scored.'
									: detail.findings && detail.findings.promoted > 0
										? /* NOT the same as "nothing needed review". Every finding
										     this import raised has been sent to live triage, so the
										     list is empty because the work moved, not because there
										     was none — saying "every one scored as approved" here
										     would be flatly untrue. */
											`All ${detail.findings.promoted} finding(s) from this import have been sent to live triage. Work them in Triage.`
										: 'No row in this import needed review. Every one scored as approved.'}
							</Empty>
						)}

						{/* CORRECTIONS. A re-score, not an edit — see the note below,
						    which is the thing a client has to understand before using
						    this. Only offered once the import has finished: the runner
						    is still writing rows for these subjects while it runs. */}
						{!isRunning(detail) && (
							<>
								<div className="grp">Correct rows</div>
								<div className="hist-note">
									Upload a CSV of corrected rows, keyed by <span className="mono">txnId</span>.
									Correcting a row RE-SCORES that subject from that date forward, because
									several rules read each subject&apos;s own recent history — so a corrected
									amount changes what the transactions after it should have scored.
									A transaction id, subject or date cannot be corrected: those identify the
									row, so changing one is a different transaction and means deleting the
									import and re-uploading.
								</div>
								<FileDrop
									accept=".csv,text/csv"
									file={correctFile}
									onFile={f => {
										setCorrectFile(f);
										setCorrectResult(null);
									}}
									disabled={correctBusy}
									label="Drag the corrections .csv here, or click to choose"
									hint="Same columns as the import; only the rows you are correcting"
								/>
								<div className="hist-how__acts">
									<button
										className="dbtn pri"
										type="button"
										disabled={!correctFile || correctBusy}
										onClick={async () => {
											if (!correctFile) return;
											setCorrectBusy(true);
											try {
												const text = await correctFile.text();
												const res: any = await correct(detail._id, text);
												setCorrectResult((res?.data ?? res)?.correction ?? null);
												setCorrectFile(null);
											} finally {
												setCorrectBusy(false);
											}
										}}
									>
										{correctBusy ? 'Re-scoring…' : 'Apply corrections'}
									</button>
								</div>

								{correctResult && (
									<div className="hist-err">
										<b>
											{correctResult.applied} corrected · {correctResult.cascaded} re-scored
											as a consequence
										</b>
										{!correctResult.changes?.length && (
											<span>No score or status changed.</span>
										)}
										{(correctResult.changes ?? []).map((c: any) => (
											<span key={c.txnId} className="mono">
												{c.corrected ? 'corrected' : 'cascaded'} {c.txnId}: score{' '}
												{c.scoreBefore}→{c.scoreAfter}
												{c.statusBefore !== c.statusAfter &&
													` · ${c.statusBefore}→${c.statusAfter}`}
											</span>
										))}
										{(correctResult.refused ?? []).map((r: any) => (
											<span key={r.txnId} className="rd">
												refused {r.txnId}: {r.reason}
											</span>
										))}
									</div>
								)}
							</>
						)}

						<div className="cfg-note">
							{detail.findings && detail.findings.promoted > 0 ? (
								<>
									<b>This import can no longer be deleted.</b> {detail.findings.promoted}{' '}
									finding(s) from it are now live triage work — deleting the import would
									delete an alert somebody is working on.
								</>
							) : (
								'Deleting this import removes every transaction and finding it added, and nothing else.'
							)}
						</div>
					</div>
				)}
			</Modal>

			{/* ── FX rates ───────────────────────────────────────────────────── */}
			<Modal
				open={fxOpen}
				title="Currency conversion rates"
				size="wide"
				onClose={() => {
					setFxOpen(false);
					setFxMsg(null);
					setFxCsv('');
				}}
				sub="Used only when an imported row has no converted amount of its own"
			>
				<div className="hist-up">
					<div className="hist-note">
						Every amount-threshold rule reads the reporting-currency figure, so a row in
						another currency needs one. If your export already carries{' '}
						<span className="mono">preferredCurrencyAmount</span>, that is used and these
						rates are never consulted — yours is the rate your books actually used.
						<br />
						<br />
						Rates are <b>effective-dated</b>: each applies from its date until the next one
						for the same pair replaces it. That matters for history — a 2019 transaction is
						converted at the 2019 rate, not today&apos;s, because a wrong rate does not just
						misreport a total, it changes which rules fired.
					</div>

					{pairs.length > 0 && (
						<>
							<div className="grp">Configured pairs</div>
							<table className="stbl">
								<thead>
									<tr>
										<th>Pair</th>
										<th className="hr-n">Rates</th>
										<th>In force from</th>
									</tr>
								</thead>
								<tbody>
									{pairs.map(p => (
										<tr key={`${p.from}${p.to}`}>
											<td className="mono">
												{p.from} → {p.to}
											</td>
											<td className="hr-n mono">{p.count}</td>
											<td className="mono">
												{new Date(p.earliest).toISOString().slice(0, 10)}
												{p.count > 1 &&
													` … ${new Date(p.latest).toISOString().slice(0, 10)}`}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</>
					)}

					<div className="grp">Add rates</div>
					<div className="hist-note">
						One per line:{' '}
						<span className="mono">fromCurrency,toCurrency,rate,effectiveFrom</span> — for
						example <span className="mono">EUR,USD,1.12,2019-01-01</span>. Paste a treasury
						export; re-uploading a corrected one updates the same dates rather than stacking
						duplicates.
					</div>
					<textarea
						className="hist-fx"
						value={fxCsv}
						placeholder={'fromCurrency,toCurrency,rate,effectiveFrom\nEUR,USD,1.12,2019-01-01\nEUR,USD,1.08,2023-01-01'}
						onChange={e => setFxCsv(e.target.value)}
					/>
					<div className="hist-how__acts">
						<button
							className="dbtn pri"
							type="button"
							disabled={!fxCsv.trim()}
							onClick={async () => {
								setFxMsg(null);
								/* A header line is optional — a client pasting three lines out
								   of a spreadsheet should not have to remember one. */
								const body = /fromcurrency/i.test(fxCsv.split('\n')[0] ?? '')
									? fxCsv
									: `fromCurrency,toCurrency,rate,effectiveFrom\n${fxCsv}`;
								const res: any = await addRates({ csv: body });
								const d = res?.data ?? res;
								setFxMsg(
									d?.inserted !== undefined
										? `${d.inserted} added, ${d.updated} updated${
												d.rejected ? `, ${d.rejected} rejected — ${d.errors?.[0]?.reason ?? ''}` : ''
											}`
										: (res?.message ?? 'Nothing was added.')
								);
								if (d?.inserted || d?.updated) setFxCsv('');
							}}
						>
							Add rates
						</button>
					</div>
					{fxMsg && <div className="hist-err">{fxMsg}</div>}

					{rates.length > 0 && (
						<>
							<div className="grp">All rates</div>
							<div className="tscroll">
								<table className="stbl">
									<thead>
										<tr>
											<th>Pair</th>
											<th className="hr-n">Rate</th>
											<th>From</th>
											<th>Source</th>
											<th className="hf-act" />
										</tr>
									</thead>
									<tbody>
										{rates.map(r => (
											<tr key={r._id}>
												<td className="mono">
													{r.fromCurrency} → {r.toCurrency}
												</td>
												<td className="hr-n mono">{r.rate}</td>
												<td className="mono">
													{new Date(r.effectiveFrom).toISOString().slice(0, 10)}
												</td>
												<td>{r.source}</td>
												<td className="hf-act">
													<button
														className="dbtn"
														type="button"
														onClick={() => removeRate(r._id)}
													>
														Remove
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							<div className="cfg-note">
								Removing a rate changes how the NEXT import converts every transaction in
								its window. Transactions already imported keep the figure they were scored
								against — that is stored, not recomputed.
							</div>
						</>
					)}
				</div>
			</Modal>

			{/* ── delete confirmation ────────────────────────────────────────── */}
			<Modal
				open={!!deleting}
				title="Delete import"
				size="narrow"
				onClose={() => {
					setConfirmDeleteId(null);
					setDeleteError(null);
				}}
				sub={deleting ? deleting.label : ''}
				footer={
					deleting && (
						<>
							<button className="dbtn" type="button" onClick={() => setConfirmDeleteId(null)}>
								Cancel
							</button>
							<button
								className="dbtn danger"
								type="button"
								style={{ marginLeft: 'auto' }}
								disabled={busy === deleting._id}
								onClick={async () => {
									setBusy(deleting._id);
									setDeleteError(null);
									try {
										const res: any = await destroy(deleting._id);
										const body = res?.data ?? res;
										/* The service refuses this once a finding has been
										   promoted, and answers with the reason rather than a
										   bare failure. Kept on screen: the reason is the whole
										   answer, and it tells the client what to do next. */
										if (!body || body.deleted !== true) {
											setDeleteError(
												res?.message ??
													'The import could not be deleted. Refresh and try again.'
											);
											return;
										}
										setConfirmDeleteId(null);
										if (viewId === deleting._id) setViewId(null);
									} finally {
										setBusy(null);
									}
								}}
							>
								Delete import
							</button>
						</>
					)
				}
			>
				{deleting && (
					<div className="hist-detail">
						<p>
							This removes the <b>{deleting.rowsImported.toLocaleString()}</b> transaction(s)
							this import added, and any findings raised from them. Nothing else in
							transaction monitoring is touched.
						</p>
						<p>
							It is refused if any finding from this import has been sent to live triage —
							that alert is open work now, and deleting it out from under whoever has it
							would be worse than leaving the import in place.
						</p>
						{deleteError && (
							<div className="hist-err">
								<b>Not deleted.</b>
								{deleteError}
							</div>
						)}
					</div>
				)}
			</Modal>
		</div>
	);
};

export default HistoricalDeck;
